/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Pavel Režňák (reznak@cadwork.cz)
 *
 */

// jscs:disable requireCamelCaseOrUpperCaseIdentifiers

import WebGLRenderGraph from "App/render/webgl/render_graph";
import Pass from "App/render/webgl/passes/pass";
import WebGLContext from "App/web_gl_context";

import FloorPLaneShader from "App/render/webgl/shaders/environment/floor_plan";
import {Containers, Factory} from "App/utils/factory";
import {Framebuffer} from "App/utils/frame_buffer";
import View from "App/view/view";
import {OpaqueBinTypeName, OpaqueNoTextureBinTypeName} from "App/render/webgl/nodes/bins/type_names";
import IScene from "js_interface/src/scene_interface";

/**
 * Orthogonal camera from top to down with limited Y (above is cut off)
 *
 * @param {SceneAPI.Scene} aSceneAPI
 * @param {WebGLRenderGraph} aRenderGraph
 * @constructor
 */
export default
class FloorPlan extends Pass {

    public static TypeName: string = "FloorPlan";
    private _shader: FloorPLaneShader;

    constructor(aSceneAPI: IScene, aRenderGraph: WebGLRenderGraph) {
        super(aSceneAPI, aRenderGraph);
    }


    public static create(aSceneAPI: IScene, aRenderGraph: WebGLRenderGraph): FloorPlan {
        return new FloorPlan(aSceneAPI, aRenderGraph);
    }

    public initialize() {
        let shaders = this._renderGraph.getLayer('environment').getShaders();
        this._shader = shaders.get(FloorPLaneShader.TypeName);
        this._shadersToBins = {};

        this.attachShaderToBin(OpaqueBinTypeName, this._shader);
        this.attachShaderToBin(OpaqueNoTextureBinTypeName, this._shader);
    }

    /**
     * @param {View} aView
     * @param {Framebuffer} aOutput float texture with single channel (floatToVec4 doesn't work)
     */
    public render(aView: View, aOutput: Framebuffer) {
        /* jshint camelcase:false */
        let scale = aOutput.getParameters().scale;
        let gl = (<WebGLContext>this._context).getWebGLRenderingContext();
        let position = aView.getPosition();
        let size = aView.getSize();

        // Set rendering target
        aOutput.bind();

        // Set viewport
        gl.enable(gl.SCISSOR_TEST);
        gl.viewport(position[0] * scale, position[1] * scale, size[0] * scale, size[1] * scale);
        gl.scissor(position[0] * scale, position[1] * scale, size[0] * scale, size[1] * scale);

        gl.clearColor(1.0, 1.0, 1.0, 1.0);
        gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
        gl.disable(gl.CULL_FACE);
        this.view = aView;

        // Bind shader
        let shader: any = this._shader.get(0);
        shader.render();

        // Get special camera projection
        gl.uniformMatrix4fv(shader.uniformLocations.u_floorMatrix, false, aView.getCamera().getFloorOrthogonalMatrix());

        // Render all the bins related to the depth pass
        let bins = this._renderGraph.getRenderBins();
        bins.get(OpaqueBinTypeName).render(this);
        bins.get(OpaqueNoTextureBinTypeName).render(this);

        gl.clearColor(0.0, 0.0, 0.0, 0.0);
        gl.disable(gl.SCISSOR_TEST);
        gl.enable(gl.CULL_FACE);
        shader.finishRender();
        aOutput.unbind(this._typeName);
        this.view = null;
    }
}

Factory.register(Containers.PASSES_ENVIRONMENT, FloorPlan);